// PobDatewiseData.jsx
import {
  X,
  FileText,
  User,
  Clock,
  DollarSign,
  Building,
  MapPin,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import * as XLSX from "xlsx";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MapViewModal from "../../pages/EmployeeMapTracker";
import axios from "axios";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";

/**
 * PobDatewiseData
 * - Clear function names and simpler structure
 * - Keeps original data shape handling
 * - Persists last loaded data in sessionStorage so returning to this page doesn't reset
 * - Adds Print (builds simple printable sheet) + Export To Excel buttons
 */

const STORAGE_KEY = "pobDatewiseData_v1";

const PobDatewiseData = ({ employeeId: propEmployeeId, date: propDate, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const qs = new URLSearchParams(location.search);

  // Priority: query params -> props
  const employeeIdFromQuery = qs.get("employee_id") || qs.get("employeeId");
  const dateFromQuery = qs.get("date");

  const employeeId = employeeIdFromQuery || propEmployeeId;
  const date = dateFromQuery || propDate;

  // UI state
  const [showMap, setShowMap] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Data state
  const [visitData, setVisitData] = useState(null); // { employee, summary: [] }
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // --- Persist / restore logic ---
  // Restore previous data from sessionStorage if it matches employeeId + date
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.employeeId === employeeId && parsed.date === date && parsed.data) {
          setVisitData(parsed.data);
          setIsLoading(false);
          setErrorMessage(null);
          return; // skip fetch - we restored cached data
        }
      }
    } catch (err) {
      console.warn("Could not restore PobDatewiseData from sessionStorage", err);
    }

    // otherwise fetch live
    fetchVisitSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, date]);

  // Save current visitData to sessionStorage for persistence
  const persistVisitDataToSession = (dataObj) => {
    try {
      const payload = {
        employeeId,
        date,
        data: dataObj,
        savedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      console.warn("Could not persist PobDatewiseData", err);
    }
  };

  // --- Fetching and normalization ---
  async function fetchVisitSummary() {
    if (!employeeId || !date) {
      setIsLoading(false);
      setErrorMessage("Missing employee id or date");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      // normalize date to YYYY-MM-DD
      const normalizedDate = new Date(date).toISOString().split("T")[0];

      const res = await axios.get(
        `${BASE_URL}/api/employees/${encodeURIComponent(employeeId)}/visit-full-summary?date=${encodeURIComponent(normalizedDate)}`,
        { headers: getAuthHeaders() }
      );

      const resp = res.data || {};

      // Try to find employee and summary in common shapes
      const employeeObj = resp.employee || resp.data?.employee || resp.data?.emp || resp;
      const summaryRaw = resp.summary || resp.data?.summary || resp.data?.visit_summary || null;

      const summaryArr = Array.isArray(summaryRaw)
        ? summaryRaw
        : summaryRaw
        ? [summaryRaw]
        : [];

      const normalized = {
        employee: employeeObj || { name: "N/A" },
        summary: summaryArr,
      };

      setVisitData(normalized);
      persistVisitDataToSession(normalized);
    } catch (err) {
      console.error("Error fetching visit details", err);
      setErrorMessage("Failed to fetch visit details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  // --- formatting helpers ---
  const formatCurrency = (value) => {
    return value ? `₹${Number.parseFloat(value).toFixed(2)}` : "₹0.00";
  };

  // --- map modal handler ---
  const openMapModalForItem = (type, item) => {
    const hasCoords =
      item &&
      ((item.latitude != null && item.longitude != null) ||
        (item.coordinates && item.coordinates.lat != null && item.coordinates.lng != null));

    if (hasCoords) {
      const lat = item.latitude != null ? Number.parseFloat(item.latitude) : Number.parseFloat(item.coordinates.lat);
      const lng = item.longitude != null ? Number.parseFloat(item.longitude) : Number.parseFloat(item.coordinates.lng);

      setSelectedLocation({
        type,
        name: item.name || "Unknown",
        coordinates: { lat, lng },
        time: item.time || item.server_time || "N/A",
        remarks: item.remarks || "N/A",
      });
    } else {
      const dummy = { lat: 28.6139 + Math.random() * 0.01, lng: 77.209 + Math.random() * 0.01 };
      setSelectedLocation({
        type,
        name: item?.name || "Unknown",
        coordinates: item?.coordinates || dummy,
        time: item?.time || item?.server_time || "N/A",
        remarks: item?.remarks || "N/A",
      });
    }

    setShowMap(true);
  };

  // --- Excel export (kept your detailed sheets) ---
  const exportToExcel = () => {
    try {
      if (!visitData) return;

      const workbook = XLSX.utils.book_new();
      const { employee, summary } = visitData;
      const dayData = summary[0] || {};

      // Employee Info sheet
      const employeeData = [
        [
          "HeadQuarter",
          "Working Date",
          "Filled Date",
          "Route In TP",
          "Route Covered",
          "Area Covered",
          "Remarks Of Day",
        ],
        [
          employee?.headquarter || "N/A",
          dayData.date || "N/A",
          dayData.filled_time || "N/A",
          dayData.route_in_tp || "N/A",
          Array.isArray(dayData.routes) ? dayData.routes.join(", ") : dayData.routes || "N/A",
          dayData.area_covered || "N/A",
          dayData.remarks || "N/A",
        ],
      ];
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(employeeData), "Employee Info");

      // Doctors sheet
      if (Array.isArray(dayData.doctors) && dayData.doctors.length > 0) {
        const doctorsData = [
          ["S.No", "Doctor", "Area", "Working With", "Time", "Server Time", "Filled", "POB", "Doctor Remarks"],
          ...dayData.doctors.map((doc, idx) => [
            idx + 1,
            doc.name || "N/A",
            doc.area || "N/A",
            doc.working_with || "Individual",
            doc.time || "N/A",
            doc.server_time || "N/A",
            doc.filled || "Mobile",
            Number.parseFloat(doc.pob) || 0,
            doc.remarks || "N/A",
          ]),
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "Total",
            dayData.doctors.reduce((sum, d) => sum + (Number.parseFloat(d.pob) || 0), 0),
            "",
          ],
        ];
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(doctorsData), "Doctors Visited");
      }

      // Chemists sheet
      if (Array.isArray(dayData.chemists) && dayData.chemists.length > 0) {
        const chemistsData = [
          ["S.No", "Chemist", "Area", "Working With", "Time", "Server Time", "Filled", "POB", "Chemist Remarks"],
          ...dayData.chemists.map((chem, idx) => [
            idx + 1,
            chem.name || "N/A",
            chem.area || "N/A",
            chem.working_with || "Individual",
            chem.time || "N/A",
            chem.server_time || "N/A",
            chem.filled || "Mobile",
            Number.parseFloat(chem.pob) || 0,
            chem.remarks || "N/A",
          ]),
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "Total",
            dayData.chemists.reduce((sum, c) => sum + (Number.parseFloat(c.pob) || 0), 0),
            "",
          ],
        ];
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(chemistsData), "Chemists Visited");
      }

      // Stockists sheet
      if (Array.isArray(dayData.stockists) && dayData.stockists.length > 0) {
        const stockistsData = [
          ["S.No", "Stockist", "City", "Working With", "Time", "Server Time", "SOB", "Remarks"],
          ...dayData.stockists.map((stk, idx) => [
            idx + 1,
            stk.name || "N/A",
            stk.city || "N/A",
            stk.working_with || "Individual",
            stk.time || "N/A",
            stk.server_time || "N/A",
            Number.parseFloat(stk.sob) || 0,
            stk.remarks || "N/A",
          ]),
        ];
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(stockistsData), "Stockists Visited");
      }

      // Distributors sheet
      if (Array.isArray(dayData.distributors) && dayData.distributors.length > 0) {
        const distributorsData = [
          ["S.No", "Distributor", "City", "Working With", "Time", "Server Time", "SOB", "Remarks"],
          ...dayData.distributors.map((dist, idx) => [
            idx + 1,
            dist.name || "N/A",
            dist.city || "N/A",
            dist.working_with || "Individual",
            dist.time || "N/A",
            dist.server_time || "N/A",
            Number.parseFloat(dist.sob) || 0,
            dist.remarks || "N/A",
          ]),
        ];
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(distributorsData), "Distributors Visited");
      }

      // Expense sheet
      const expenseData = [
        [
          "S.No",
          "Route",
          "Type",
          "Distance",
          "TA",
          "DA (HQ)",
          "DA (Ex-Station)",
          "DA (Out-Station)",
          "Hotel Charges",
          "Other Charges",
          "Total",
          "Remarks",
        ],
        [
          1,
          dayData.expense?.route || (Array.isArray(dayData.routes) ? dayData.routes.join(", ") : dayData.routes) || "N/A",
          dayData.expense?.type || "Ex Station",
          Number.parseFloat(dayData.expense?.distance) || 0,
          Number.parseFloat(dayData.expense?.ta) || 0,
          Number.parseFloat(dayData.expense?.da_hq) || 0,
          Number.parseFloat(dayData.expense?.da_exstation) || 0,
          Number.parseFloat(dayData.expense?.da_outstation) || 0,
          Number.parseFloat(dayData.expense?.hotel_charges) || 0,
          Number.parseFloat(dayData.expense?.other_charges) || 0,
          Number.parseFloat(dayData.expense?.total) || 0,
          dayData.expense?.remarks || "N/A",
        ],
      ];
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(expenseData), "Expense Details");

      const fileName = `DCR_${(employee?.name || "Employee").replace(/\s+/g, "_")}_${dayData.date || new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error("Error exporting to Excel:", err);
      alert("Error exporting to Excel. Please try again.");
    }
  };

  // --- Print building function: build a simple printable sheet from visitData ---
  const printReport = () => {
    const escapeHtml = (str) => {
      if (str === null || str === undefined) return "";
      return String(str)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
    };

    if (!visitData) {
      const w = window.open("", "_blank", "width=900,height=700");
      if (!w) return;
      w.document.write("<p>No data to print</p>");
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 200);
      return;
    }

    const { employee, summary } = visitData;
    const dayData = summary[0] || {};

    // Helper to build a small table HTML from a 2D array
    const buildTable = (rows) =>
      `<table style="border-collapse:collapse;width:100%;margin-bottom:12px">
        ${rows
          .map(
            (r, idx) =>
              `<tr>${r
                .map(
                  (c) =>
                    `<td style="border:1px solid #ddd;padding:6px;vertical-align:top;font-size:12px">${escapeHtml(c)}</td>`
                )
                .join("")}</tr>`
          )
          .join("")}
      </table>`;

    // Build sections
    const headerHtml = `<h2>Daily Call Report - ${escapeHtml(dayData.date || date || "")}</h2>
      <div style="margin-bottom:8px;font-size:13px;color:#333">
        Employee: <strong>${escapeHtml(employee?.name || "N/A")}</strong> |
        Designation: <strong>${escapeHtml(employee?.designation || "N/A")}</strong> |
        HQ: <strong>${escapeHtml(employee?.headquarter || "N/A")}</strong>
      </div>`;

    const employeeInfoRows = [
      ["HeadQuarter", "Working Date", "Filled Date", "Route In TP", "Route Covered", "Area Covered", "Remarks Of Day"],
      [
        employee?.headquarter || "N/A",
        dayData.date || "N/A",
        dayData.filled_time || "N/A",
        dayData.route_in_tp || "N/A",
        Array.isArray(dayData.routes) ? dayData.routes.join(", ") : dayData.routes || "N/A",
        dayData.area_covered || "N/A",
        dayData.remarks || "N/A",
      ],
    ];

    const doctorsRows = (Array.isArray(dayData.doctors) && dayData.doctors.length > 0)
      ? [
          ["S.No", "Doctor", "Area", "Working With", "Time", "Server Time", "Filled", "POB", "Doctor Remarks"],
          ...dayData.doctors.map((d, i) => [
            i + 1,
            d.name || "N/A",
            d.area || "N/A",
            d.working_with || "Individual",
            d.time || "N/A",
            d.server_time || "N/A",
            d.filled || "Mobile",
            d.pob ?? "0",
            d.remarks || "N/A",
          ]),
        ]
      : null;

    const chemistsRows = (Array.isArray(dayData.chemists) && dayData.chemists.length > 0)
      ? [
          ["S.No", "Chemist", "Area", "Working With", "Time", "Server Time", "Filled", "POB", "Chemist Remarks"],
          ...dayData.chemists.map((c, i) => [
            i + 1,
            c.name || "N/A",
            c.area || "N/A",
            c.working_with || "Individual",
            c.time || "N/A",
            c.server_time || "N/A",
            c.filled || "Mobile",
            c.pob ?? "0",
            c.remarks || "N/A",
          ]),
        ]
      : null;

    const stockistsRows = (Array.isArray(dayData.stockists) && dayData.stockists.length > 0)
      ? [
          ["S.No", "Stockist", "City", "Working With", "Time", "Server Time", "SOB", "Remarks"],
          ...dayData.stockists.map((s, i) => [
            i + 1,
            s.name || "N/A",
            s.city || "N/A",
            s.working_with || "Individual",
            s.time || "N/A",
            s.server_time || "N/A",
            s.sob ?? "0",
            s.remarks || "N/A",
          ]),
        ]
      : null;

    const distributorsRows = (Array.isArray(dayData.distributors) && dayData.distributors.length > 0)
      ? [
          ["S.No", "Distributor", "City", "Working With", "Time", "Server Time", "SOB", "Remarks"],
          ...dayData.distributors.map((d, i) => [
            i + 1,
            d.name || "N/A",
            d.city || "N/A",
            d.working_with || "Individual",
            d.time || "N/A",
            d.server_time || "N/A",
            d.sob ?? "0",
            d.remarks || "N/A",
          ]),
        ]
      : null;

    const expenseRows = [
      ["S.No", "Route", "Type", "Distance", "TA", "DA (HQ)", "DA (Ex-Station)", "DA (Out-Station)", "Hotel Charges", "Other Charges", "Total", "Remarks"],
      [
        1,
        dayData.expense?.route || (Array.isArray(dayData.routes) ? dayData.routes.join(", ") : dayData.routes) || "N/A",
        dayData.expense?.type || "Ex Station",
        dayData.expense?.distance ?? "0.00",
        dayData.expense?.ta ?? "0.00",
        dayData.expense?.da_hq ?? "0.00",
        dayData.expense?.da_exstation ?? "0.00",
        dayData.expense?.da_outstation ?? "0.00",
        dayData.expense?.hotel_charges ?? "0.00",
        dayData.expense?.other_charges ?? "0.00",
        dayData.expense?.total ?? "0.00",
        dayData.expense?.remarks || "N/A",
      ],
    ];

    // Compose HTML
    let bodyHtml = headerHtml;
    bodyHtml += "<h4>Employee Info</h4>" + buildTable(employeeInfoRows);
    if (doctorsRows) {
      bodyHtml += "<h4>Doctors Visited</h4>" + buildTable(doctorsRows);
    } else {
      bodyHtml += "<h4>Doctors Visited</h4><p>No doctors visited</p>";
    }
    if (chemistsRows) bodyHtml += "<h4>Chemists Visited</h4>" + buildTable(chemistsRows);
    if (stockistsRows) bodyHtml += "<h4>Stockists Visited</h4>" + buildTable(stockistsRows);
    if (distributorsRows) bodyHtml += "<h4>Distributors Visited</h4>" + buildTable(distributorsRows);
    bodyHtml += "<h4>Expense Details</h4>" + buildTable(expenseRows);

    const style = `
      body{font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial; margin:18px; color:#111}
      h2{margin-bottom:6px}
      h4{margin-top:14px;margin-bottom:8px}
      table{border-collapse:collapse;width:100%}
      th,td{border:1px solid #e5e7eb;padding:6px;font-size:12px;text-align:left}
    `;

    const w = window.open("", "_blank", "width=1000,height=800");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>DCR - ${escapeHtml(dayData.date || date || "")}</title><style>${style}</style></head><body>`);
    w.document.write(bodyHtml);
    w.document.write("</body></html>");
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 250);
  };

  // Early loading UI
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <p className="mt-2 text-gray-600">Loading visit details...</p>
      </div>
    );
  }

  // Error UI
  if (errorMessage) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4">
        <div className="bg-red-100 text-red-700 p-4 rounded-lg max-w-md text-center">
          <p>{errorMessage}</p>
          {typeof onClose === "function" && (
            <button onClick={onClose} className="mt-4 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded">
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!visitData) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-gray-500">No data available</p>
        {typeof onClose === "function" && (
          <button onClick={onClose} className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded">
            Close
          </button>
        )}
      </div>
    );
  }

  const { employee, summary } = visitData;
  const dayData = summary[0] || {};

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b-2 border-gray-200 p-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gray-800 mb-1">
              DCR Preview - {employee?.name || "Employee"} ({employee?.designation || "N/A"}) {employee?.headquarter || "N/A"}
            </h1>
            <p className="text-sm text-gray-600">Date: {dayData?.date || date || "N/A"}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Employee Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <User className="h-5 w-5 text-indigo-600" />
                Employee Information
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-50 to-indigo-50">
                    <th className="border-r border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">HeadQuarter</th>
                    <th className="border-r border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">Working Date</th>
                    <th className="border-r border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">Filled Date</th>
                    <th className="border-r border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">Route In TP</th>
                    <th className="border-r border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">Route Covered</th>
                    <th className="border-r border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">Area Covered</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Remarks Of Day</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-gray-50">
                    <td className="border-r border-gray-200 px-4 py-3">{employee?.headquarter || "N/A"}</td>
                    <td className="border-r border-gray-200 px-4 py-3">{dayData.date || "N/A"}</td>
                    <td className="border-r border-gray-200 px-4 py-3">{dayData.filled_time || "N/A"}</td>
                    <td className="border-r border-gray-200 px-4 py-3">{dayData.route_in_tp || "N/A"}</td>
                    <td className="border-r border-gray-200 px-4 py-3">{Array.isArray(dayData.routes) ? dayData.routes.join(", ") : dayData.routes || "N/A"}</td>
                    <td className="border-r border-gray-200 px-4 py-3">{dayData.area_covered || "N/A"}</td>
                    <td className="px-4 py-3">{dayData.remarks || "N/A"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Doctors */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <User className="h-5 w-5 text-green-600" />
                Doctors Visited ({dayData.doctors?.length || 0})
              </h3>
            </div>
            {Array.isArray(dayData.doctors) && dayData.doctors.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-green-50 to-emerald-50">
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">S.No</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Doctor</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Area</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Working With</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Time</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Server Time</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Filled</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">POB</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Doctor Remarks</th>
                      <th className="px-3 py-3 text-left font-semibold text-gray-700">Map</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayData.doctors.map((doctor, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 border-b border-gray-100">
                        <td className="border-r border-gray-200 px-3 py-3 text-center font-medium">{idx + 1}</td>
                        <td className="border-r border-gray-200 px-3 py-3 font-medium text-gray-900">{doctor.name || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{doctor.area || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{doctor.working_with || "Individual"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{doctor.time || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{doctor.server_time || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{doctor.filled || "Mobile"}</td>
                        <td className="border-r border-gray-200 px-3 py-3 font-semibold text-green-600">{doctor.pob || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{doctor.remarks || "N/A"}</td>
                        <td className="px-3 py-3">
                          <button onClick={() => openMapModalForItem("doctor", doctor)} className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 font-medium">No Doctor Visited</p>
              </div>
            )}
          </div>

          {/* Chemists */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Building className="h-5 w-5 text-teal-600" />
                Chemists Visited ({dayData.chemists?.length || 0})
              </h3>
            </div>
            {Array.isArray(dayData.chemists) && dayData.chemists.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-teal-50 to-cyan-50">
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">S.No</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Chemist</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Area</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Working With</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Time</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Server Time</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Filled</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">POB</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Chemist Remarks</th>
                      <th className="px-3 py-3 text-left font-semibold text-gray-700">Map</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayData.chemists.map((chemist, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 border-b border-gray-100">
                        <td className="border-r border-gray-200 px-3 py-3 text-center font-medium">{idx + 1}</td>
                        <td className="border-r border-gray-200 px-3 py-3 font-medium text-gray-900">{chemist.name || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{chemist.area || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{chemist.working_with || "Individual"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{chemist.time || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{chemist.server_time || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{chemist.filled || "Mobile"}</td>
                        <td className="border-r border-gray-200 px-3 py-3 font-semibold text-teal-600">{chemist.pob || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{chemist.remarks || "N/A"}</td>
                        <td className="px-3 py-3">
                          <button onClick={() => openMapModalForItem("chemist", chemist)} className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Building className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 font-medium">No Chemist Visited</p>
              </div>
            )}
          </div>

          {/* Stockists */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Building className="h-5 w-5 text-yellow-600" />
                Stockists Visited ({dayData.stockists?.length || 0})
              </h3>
            </div>
            {Array.isArray(dayData.stockists) && dayData.stockists.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-yellow-50 to-amber-50">
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">S.No</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Stockist</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">City</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Working With</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Time</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Server Time</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">SOB</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Remarks</th>
                      <th className="px-3 py-3 text-left font-semibold text-gray-700">Map</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayData.stockists.map((stockist, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 border-b border-gray-100">
                        <td className="border-r border-gray-200 px-3 py-3 text-center font-medium">{idx + 1}</td>
                        <td className="border-r border-gray-200 px-3 py-3 font-medium text-gray-900">{stockist.name || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{stockist.city || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{stockist.working_with || "Individual"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{stockist.time || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{stockist.server_time || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3 font-semibold text-yellow-600">{stockist.sob || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{stockist.remarks || "N/A"}</td>
                        <td className="px-3 py-3">
                          <button onClick={() => openMapModalForItem("stockist", stockist)} className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Building className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 font-medium">No Stockist Visited</p>
              </div>
            )}
          </div>

          {/* Distributors */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50 to-violet-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Building className="h-5 w-5 text-purple-600" />
                Distributor Visited ({dayData.distributors?.length || 0})
              </h3>
            </div>
            {Array.isArray(dayData.distributors) && dayData.distributors.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-purple-50 to-violet-50">
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">S.No</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Distributor</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">City</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Working With</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Time</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Server Time</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">SOB</th>
                      <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Remarks</th>
                      <th className="px-3 py-3 text-left font-semibold text-gray-700">Map</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayData.distributors.map((distributor, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 border-b border-gray-100">
                        <td className="border-r border-gray-200 px-3 py-3 text-center font-medium">{idx + 1}</td>
                        <td className="border-r border-gray-200 px-3 py-3 font-medium text-gray-900">{distributor.name || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{distributor.city || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{distributor.working_with || "Individual"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{distributor.time || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{distributor.server_time || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3 font-semibold text-purple-600">{distributor.sob || "N/A"}</td>
                        <td className="border-r border-gray-200 px-3 py-3">{distributor.remarks || "N/A"}</td>
                        <td className="px-3 py-3">
                          <button onClick={() => openMapModalForItem("distributor", distributor)} className="bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Building className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 font-medium">No Distributor Visited</p>
              </div>
            )}
          </div>

          {/* Expense */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-red-50 to-pink-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-red-600" />
                Expense Details
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-red-50 to-pink-50">
                    <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">S.No</th>
                    <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Route</th>
                    <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Type</th>
                    <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Distance</th>
                    <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">TA</th>
                    <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">DA (HQ)</th>
                    <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">DA (Ex-Station)</th>
                    <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">DA (Out-Station)</th>
                    <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Hotel Charges</th>
                    <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Other Charges</th>
                    <th className="border-r border-gray-200 px-3 py-3 text-left font-semibold text-gray-700">Total</th>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-gray-50">
                    <td className="border-r border-gray-200 px-3 py-3 text-center font-medium">1</td>
                    <td className="border-r border-gray-200 px-3 py-3">{dayData.expense?.route || (Array.isArray(dayData.routes) ? dayData.routes.join(", ") : dayData.routes) || "N/A"}</td>
                    <td className="border-r border-gray-200 px-3 py-3">{dayData.expense?.type || "N/A"}</td>
                    <td className="border-r border-gray-200 px-3 py-3">{dayData.expense?.distance ?? "0.00"}</td>
                    <td className="border-r border-gray-200 px-3 py-3">{dayData.expense?.ta ?? "0.00"}</td>
                    <td className="border-r border-gray-200 px-3 py-3">{dayData.expense?.da_hq ?? "0.00"}</td>
                    <td className="border-r border-gray-200 px-3 py-3">{dayData.expense?.da_exstation ?? "0.00"}</td>
                    <td className="border-r border-gray-200 px-3 py-3">{dayData.expense?.da_outstation ?? "0.00"}</td>
                    <td className="border-r border-gray-200 px-3 py-3">{dayData.expense?.hotel_charges ?? "0.00"}</td>
                    <td className="border-r border-gray-200 px-3 py-3">{dayData.expense?.other_charges ?? "0.00"}</td>
                    <td className="border-r border-gray-200 px-3 py-3">{dayData.expense?.total ?? "0.00"}</td>
                    <td className="px-3 py-3">{dayData.expense?.remarks || "N/A"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="h-4 w-4" />
          <span>Generated on {new Date().toLocaleDateString()}</span>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/pobdatewise")}
            className="bg-white border border-gray-300 px-4 py-2 rounded-md shadow-sm flex items-center gap-2 hover:bg-gray-50"
            title="Return to list"
          >
            <ArrowLeft className="h-4 w-4" />
            Return
          </button>

          <button
            onClick={printReport}
            className="bg-white border border-gray-300 px-4 py-2 rounded-md shadow-sm flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Print
          </button>

          <button
            onClick={exportToExcel}
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 shadow-sm hover:shadow-md"
          >
            <FileText className="h-4 w-4" />
            Export To Excel
          </button>
        </div>
      </div>

      {/* Map Modal */}
      {showMap && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <MapViewModal
              location={selectedLocation}
              onClose={() => setShowMap(false)}
              employeeId={employeeId}
              date={date}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PobDatewiseData;
